import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Box, Paper, Avatar, Typography, Button, Modal, Fade, IconButton, TextField, useTheme, Table, TableBody, TableCell, TableContainer, TableRow, Checkbox, FormControlLabel } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { Close, ArrowForward, ArrowBack } from "@material-ui/icons";
import { getLoggedInUserId } from "../../../store/hub/loggedInUser";
import { getUserById, getUsers, getUserImageById } from "../../../store/hub/users";
import { getBuzzBucksBalanceByUserId, getBuzzBucksMainPool, getBuzzBucksDistributorPool, getBuzzBucksUserHistory } from "../../../store/hub/buzzBucks";
import BuzzHubsIcon from "../../../assets/images/logo_small.png";
import { addSnackbarIssue } from "../../../components/common/StoreCommon";
import { convertDateTime } from "../../../components/common/Common";
import PermissionService from "../../../services/PermissionService";
import { hubDistributeBuzzBucksFromMainPool, hubDistributeBuzzBucksToUser, hubUserTradeIn } from "../../../components/common/ConnectionMiddleware";
import { setNewFeedPostVisibilityInStore, setNewFeedPostDefaultTextInStore } from "../../../components/common/StoreFeedUi";
import UserBuzzBucks from "./UserBuzzBucks";
import MainPoolDetails from "./MainPoolDetails";
import DistributorDetails from "./DistributorDetails";
import tradeInImage from "../../../assets/images/trade-in-buzzbucks.png";

export default function BuzzBucksView() {
  const classes = useStyles();
  const theme = useTheme();
  const permissionService = new PermissionService();
  const loggedInUserId = useSelector(getLoggedInUserId)();
  const loggedInUser = useSelector(getUserById)(loggedInUserId);
  const getBuzzBucksBalance = useSelector(getBuzzBucksBalanceByUserId);
  const userBalance = getBuzzBucksBalance(loggedInUserId);
  const mainPool = useSelector(getBuzzBucksMainPool);
  const distributorPool = useSelector(getBuzzBucksDistributorPool);
  const users = useSelector(getUsers)();
  const userHistory = useSelector(getBuzzBucksUserHistory);
  const getUserImageByIdSelector = useSelector(getUserImageById);
  
  const isPoolManager = permissionService.bbPoolManagerFl();
  const isDistributor = permissionService.bbDistributorFl();
  
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAmount, setModalAmount] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [currentViewIndex, setCurrentViewIndex] = useState(0);
  const [distributeModalOpen, setDistributeModalOpen] = useState(false);
  const [distributeSelectedUser, setDistributeSelectedUser] = useState(null);
  const [distributeAmount, setDistributeAmount] = useState("");
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendSelectedUser, setSendSelectedUser] = useState(null);
  const [sendAmount, setSendAmount] = useState("");
  const [sendMessage, setSendMessage] = useState("");
  const [createFeedPost, setCreateFeedPost] = useState(false);
  
  // Determine available views
  const availableViews = ["userBuzzBucks"];
  if (isPoolManager) {
    availableViews.push("mainPool");
  }
  if (isDistributor) {
    availableViews.push("distributor");
  }
  
  const handleNextView = () => {
    setCurrentViewIndex((prevIndex) => (prevIndex + 1) % availableViews.length);
  };
  
  const handlePreviousView = () => {
    setCurrentViewIndex((prevIndex) => 
      prevIndex === 0 ? availableViews.length - 1 : prevIndex - 1
    );
  };
  
  const currentView = availableViews[currentViewIndex];
  const hasMultipleViews = availableViews.length > 1;
  
  const currentBalance = userBalance?.balance || 0;
  const lifetimeReceived = userBalance?.lifetimeReceived || 0;
  const lifetimeTradedIn = userBalance?.lifetimeTradedIn || 0;
  
  const totalAmount = mainPool?.totalAmount || 0;
  const withDistributors = mainPool?.withDistributors || 0;
  const withUsers = mainPool?.withUsers || 0;
  const remainingAmount = mainPool?.remainingAmount || 0;
  
  const availableBalance = distributorPool?.availableBalance || 0;
  const totalReceived = distributorPool?.totalReceived || 0;
  const totalDistributed = distributorPool?.totalDistributed || 0;
  
  const handleOpenModal = () => {
    setModalAmount("");
    setModalMessage("");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalAmount("");
    setModalMessage("");
  };

  const handleTradeIn = async () => {
    const amountValue = parseFloat(modalAmount);
    if (modalAmount && amountValue > 0 && amountValue <= currentBalance) {
      try {
        await hubUserTradeIn(amountValue, modalMessage || "");
        handleCloseModal();
      } catch (error) {
        addSnackbarIssue(
          "Failed to trade in BuzzBucks. Please try again.",
          "error"
        );
      }
    }
  };

  const handleOpenDistributeModal = () => {
    setDistributeSelectedUser(null);
    setDistributeAmount("");
    setDistributeModalOpen(true);
  };

  const handleCloseDistributeModal = () => {
    setDistributeModalOpen(false);
    setDistributeSelectedUser(null);
    setDistributeAmount("");
  };

  const handleDistribute = async () => {
    const amountValue = parseFloat(distributeAmount);
    if (distributeSelectedUser && distributeAmount && amountValue > 0 && amountValue <= remainingAmount) {
      try {
        await hubDistributeBuzzBucksFromMainPool(distributeSelectedUser.id, amountValue);
        handleCloseDistributeModal();
      } catch (error) {
        addSnackbarIssue(
          "Failed to distribute BuzzBucks. Please try again.",
          "error"
        );
      }
    }
  };

  const distributeAmountValue = parseFloat(distributeAmount) || 0;
  const isDistributeAmountValid = distributeAmountValue > 0 && distributeAmountValue <= remainingAmount;

  const distributorUserOptions = users
    .filter(user => user.BbDistributorFl === 1 || user.BbDistributorFl === true)
    .map(user => ({
      label: user.FullName,
      id: user.Id,
      user: user
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const allUserOptions = users
    .map(user => ({
      label: user.FullName,
      id: user.Id,
      user: user
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleOpenSendModal = () => {
    setSendSelectedUser(null);
    setSendAmount("");
    setSendMessage("");
    setCreateFeedPost(false);
    setSendModalOpen(true);
  };

  const handleCloseSendModal = (shouldOpenFeedPost = false) => {
    setSendModalOpen(false);
    setSendSelectedUser(null);
    setSendAmount("");
    const notesToUse = sendMessage;
    setSendMessage("");
    const wasChecked = createFeedPost;
    setCreateFeedPost(false);
    
    // If checkbox was checked and we should open feed post, open feed post dialog after closing this modal
    if (shouldOpenFeedPost && wasChecked) {
      // Use setTimeout to ensure the modal closes before opening the feed post dialog
      setTimeout(() => {
        setNewFeedPostDefaultTextInStore(notesToUse || "");
        setNewFeedPostVisibilityInStore(true);
      }, 100);
    }
  };

  const handleSend = async () => {
    const amountValue = parseFloat(sendAmount);
    if (sendSelectedUser && sendAmount && amountValue > 0 && amountValue <= availableBalance) {
      try {
        await hubDistributeBuzzBucksToUser(sendSelectedUser.id, amountValue, sendMessage || "");
        handleCloseSendModal(true); // Pass true to indicate successful send, so feed post can be opened if checkbox was checked
      } catch (error) {
        addSnackbarIssue(
          "Failed to send BuzzBucks. Please try again.",
          "error"
        );
      }
    }
  };

  const sendAmountValue = parseFloat(sendAmount) || 0;
  const isSendAmountValid = sendAmountValue > 0 && sendAmountValue <= availableBalance;
  
  return (
    <Box
      style={{
        background: "linear-gradient(to bottom, #FFBD4F, #FF9B00)",
        minHeight: "calc(100vh - 64px)",
        width: "100%",
        padding: theme.spacing(2),
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        boxSizing: "border-box",
        overflowX: "hidden", // Prevent horizontal overflow
      }}
    >
      <Box className={classes.contentWrapper}>
        <Box className={classes.cardsContainer}>
          <Paper className={classes.profileCard} elevation={3}>
            <Box className={classes.avatarSection}>
              {hasMultipleViews && (
                <IconButton 
                  className={classes.backArrow}
                  onClick={handlePreviousView}
                  size="small"
                  color="primary"
                >
                  <ArrowBack />
                </IconButton>
              )}
              <Avatar 
                src={loggedInUser?.ImageSrc} 
                className={classes.avatar}
                alt={loggedInUser?.FirstName || "User"}
              />
              {hasMultipleViews && (
                <IconButton 
                  className={classes.forwardArrow}
                  onClick={handleNextView}
                  size="small"
                  color="primary"
                >
                  <ArrowForward />
                </IconButton>
              )}
            </Box>
            <Box className={classes.detailsSection}>
              <Typography variant="h6" className={classes.userName}>
                {currentView === "mainPool" 
                  ? "Main Pool" 
                  : currentView === "distributor"
                  ? "Distribution Pool"
                  : (loggedInUser?.FirstName || "User")}
              </Typography>
              <Box className={classes.carouselContainer}>
                {currentView === "userBuzzBucks" && (
                  <UserBuzzBucks
                    currentBalance={currentBalance}
                    lifetimeReceived={lifetimeReceived}
                    lifetimeTradedIn={lifetimeTradedIn}
                  />
                )}
                {currentView === "mainPool" && (
                  <MainPoolDetails
                    totalAmount={totalAmount}
                    withDistributors={withDistributors}
                    withUsers={withUsers}
                    remainingAmount={remainingAmount}
                    onDistributeClick={handleOpenDistributeModal}
                  />
                )}
                {currentView === "distributor" && (
                  <DistributorDetails
                    availableBalance={availableBalance}
                    totalReceived={totalReceived}
                    totalDistributed={totalDistributed}
                    onSendClick={handleOpenSendModal}
                  />
                )}
              </Box>
            </Box>
          </Paper>
          <Paper className={classes.blankCard} elevation={3}>
            <Box className={classes.tradeInContent}>
              <img 
                src={tradeInImage} 
                alt="Trade In BuzzBucks" 
                className={classes.tradeInImage}
              />
              <Button 
                variant="contained" 
                color="primary"
                className={classes.tradeInButton}
                onClick={handleOpenModal}
              >
                <Typography className={classes.buttonLabel}>Trade In</Typography>
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* History Table - sorted newest first by utcCreatedDateTime */}
        {userHistory && userHistory.length > 0 && (
          <Paper className={classes.historyCard} elevation={3}>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {[...userHistory]
                    .sort((a, b) => new Date(b.utcCreatedDateTime) - new Date(a.utcCreatedDateTime))
                    .map((item) => {
                    const detailsText = item.details || "";
                    const displayText = item.message 
                      ? `${detailsText}: ${item.message}` 
                      : detailsText;
                    
                    // Get avatar image - use BuzzHubs icon if UserAvatarId is -1 or null
                    let avatarSrc;
                    if (item.userAvatarId === -1 || !item.userAvatarId) {
                      avatarSrc = BuzzHubsIcon;
                    } else {
                      const userImage = getUserImageByIdSelector(item.userAvatarId);
                      avatarSrc = userImage && userImage !== "BuzzHubs" ? userImage : BuzzHubsIcon;
                    }
                    
                    return (
                      <TableRow key={item.id}>
                        <TableCell className={classes.avatarCell} padding="none">
                          <Avatar src={avatarSrc} className={classes.historyAvatar} />
                        </TableCell>
                        <TableCell className={classes.amountCell} align="center">
                          {item.amount}
                        </TableCell>
                        <TableCell className={classes.dateCell}>
                          {convertDateTime(item.utcCreatedDateTime, true)}
                        </TableCell>
                        <TableCell className={classes.descriptionCell}>
                          {displayText}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {/* Trade In Confirmation Modal */}
      <Modal
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        open={modalOpen}
        onClose={handleCloseModal}
        BackdropProps={{
          timeout: 500,
          style: { background: "rgba(50,50,50,0.5)" },
        }}
        aria-labelledby="Confirm Trade In"
        aria-describedby="Confirm BuzzBucks trade in"
      >
        <Fade in={modalOpen}>
          <Paper className={classes.modalPaper}>
            <Box className={classes.modalHeader}>
              <Typography variant="h6" className={classes.modalTitle}>
                Confirm Trade In
              </Typography>
              <IconButton
                style={{
                  width: 30,
                  height: 30,
                }}
                onClick={handleCloseModal}
              >
                <Close />
              </IconButton>
            </Box>
            <Box className={classes.modalContent}>
              <Box className={classes.modalForm}>
                <TextField
                  label="Amount"
                  type="number"
                  variant="outlined"
                  size="small"
                  value={modalAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow whole numbers (integers)
                    if (value === "" || /^\d+$/.test(value)) {
                      setModalAmount(value);
                    }
                  }}
                  className={classes.modalAmountInput}
                  inputProps={{ min: 1, step: 1, max: currentBalance }}
                  error={!!(modalAmount && parseFloat(modalAmount || 0) > currentBalance)}
                />
                <TextField
                  label="Message (optional)"
                  variant="outlined"
                  size="small"
                  multiline
                  minRows={4}
                  value={modalMessage}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 500) {
                      setModalMessage(value);
                    }
                  }}
                  className={classes.modalMessageInput}
                  inputProps={{ maxLength: 500 }}
                  helperText={`${modalMessage.length}/500`}
                />
              </Box>
              {modalAmount && parseFloat(modalAmount || 0) > currentBalance && (
                <Typography variant="caption" className={classes.errorMessage}>
                  Amount cannot exceed current balance ({currentBalance})
                </Typography>
              )}
              <Box className={classes.modalActions}>
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  className={classes.modalCancelButton}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleTradeIn}
                  disabled={!modalAmount || !(parseFloat(modalAmount || 0) > 0 && parseFloat(modalAmount || 0) <= currentBalance)}
                  className={classes.modalDistributeButton}
                >
                  Trade In
                </Button>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Modal>

      {/* Distribute BuzzBucks Modal */}
      <Modal
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        open={distributeModalOpen}
        onClose={handleCloseDistributeModal}
        BackdropProps={{
          timeout: 500,
          style: { background: "rgba(50,50,50,0.5)" },
        }}
        aria-labelledby="Distribute BuzzBucks"
        aria-describedby="Distribute BuzzBucks from Main Pool"
      >
        <Fade in={distributeModalOpen}>
          <Paper className={classes.modalPaper}>
            <Box className={classes.modalHeader}>
              <Typography variant="h6" className={classes.modalTitle}>
                Distribute BuzzBucks
              </Typography>
              <IconButton
                style={{
                  width: 30,
                  height: 30,
                }}
                onClick={handleCloseDistributeModal}
              >
                <Close />
              </IconButton>
            </Box>
            <Box className={classes.modalContent}>
              <Box className={classes.modalForm}>
                <Autocomplete
                  id="distribute-user-select"
                  options={distributorUserOptions}
                  getOptionLabel={(option) => option.label}
                  getOptionSelected={(option, value) => option.id === value?.id}
                  value={distributeSelectedUser}
                  onChange={(event, newValue) => {
                    setDistributeSelectedUser(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select User"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  className={classes.modalAutocomplete}
                />
                <TextField
                  label="Amount"
                  type="number"
                  variant="outlined"
                  size="small"
                  value={distributeAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow whole numbers (integers)
                    if (value === "" || /^\d+$/.test(value)) {
                      setDistributeAmount(value);
                    }
                  }}
                  className={classes.modalAmountInput}
                  inputProps={{ min: 1, step: 1, max: remainingAmount }}
                  error={!!(distributeAmount && distributeAmountValue > remainingAmount)}
                />
              </Box>
              {distributeAmount && distributeAmountValue > remainingAmount && (
                <Typography variant="caption" className={classes.errorMessage}>
                  Amount cannot exceed remaining ({remainingAmount})
                </Typography>
              )}
              <Box className={classes.modalActions}>
                <Button
                  variant="outlined"
                  onClick={handleCloseDistributeModal}
                  className={classes.modalCancelButton}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleDistribute}
                  disabled={!distributeSelectedUser || !distributeAmount || !isDistributeAmountValid}
                  className={classes.modalDistributeButton}
                >
                  Distribute
                </Button>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Modal>

      {/* Send BuzzBucks Modal */}
      <Modal
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        open={sendModalOpen}
        onClose={handleCloseSendModal}
        BackdropProps={{
          timeout: 500,
          style: { background: "rgba(50,50,50,0.5)" },
        }}
        aria-labelledby="Confirm Distribution"
        aria-describedby="Confirm BuzzBucks distribution"
      >
        <Fade in={sendModalOpen}>
          <Paper className={classes.modalPaper}>
            <Box className={classes.modalHeader}>
              <Typography variant="h6" className={classes.modalTitle}>
                Confirm Distribution
              </Typography>
              <IconButton
                style={{
                  width: 30,
                  height: 30,
                }}
                onClick={handleCloseSendModal}
              >
                <Close />
              </IconButton>
            </Box>
            <Box className={classes.modalContent}>
              <Box className={classes.modalForm}>
                <Autocomplete
                  id="send-user-select"
                  options={allUserOptions}
                  getOptionLabel={(option) => option.label}
                  getOptionSelected={(option, value) => option.id === value?.id}
                  value={sendSelectedUser}
                  onChange={(event, newValue) => {
                    setSendSelectedUser(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select User"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  className={classes.modalAutocomplete}
                />
                <TextField
                  label="Amount"
                  type="number"
                  variant="outlined"
                  size="small"
                  value={sendAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow whole numbers (integers)
                    if (value === "" || /^\d+$/.test(value)) {
                      setSendAmount(value);
                    }
                  }}
                  className={classes.modalAmountInput}
                  inputProps={{ min: 1, step: 1, max: availableBalance }}
                  error={!!(sendAmount && sendAmountValue > availableBalance)}
                />
                <TextField
                  label="Message to user (optional)"
                  variant="outlined"
                  size="small"
                  multiline
                  minRows={4}
                  value={sendMessage}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 500) {
                      setSendMessage(value);
                    }
                  }}
                  className={classes.modalMessageInput}
                  inputProps={{ maxLength: 500 }}
                  helperText={`${sendMessage.length}/500`}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={createFeedPost}
                      onChange={(e) => setCreateFeedPost(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Create Feed post"
                />
              </Box>
              {sendAmount && sendAmountValue > availableBalance && (
                <Typography variant="caption" className={classes.errorMessage}>
                  Amount cannot exceed available balance ({availableBalance})
                </Typography>
              )}
              <Box className={classes.modalActions}>
                <Button
                  variant="outlined"
                  onClick={handleCloseSendModal}
                  className={classes.modalCancelButton}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSend}
                  disabled={!sendSelectedUser || !sendAmount || !isSendAmountValid}
                  className={classes.modalDistributeButton}
                >
                  Distribute
                </Button>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%",
    maxWidth: `${400 + theme.spacing(2) + 400}px`, // Match cards container width (816px)
    alignItems: "stretch", // Stretch children to full width of wrapper
    margin: "0 auto", // Center the wrapper itself
    boxSizing: "border-box",
    // When viewport is small, allow wrapper to shrink but maintain centering
    "@media (max-width: 848px)": {
      maxWidth: "calc(100% - 0px)", // Account for any potential margin
    },
  },
  cardsContainer: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    alignItems: "stretch",
    flexWrap: "wrap", // Allow cards to wrap when they don't fit
    justifyContent: "center",
    // Calculate exact width needed: 400px + 16px gap + 400px = 816px
    // But allow it to shrink to fit parent
    width: `${400 + theme.spacing(2) + 400}px`,
    maxWidth: "100%", // Constrain to parent width
    boxSizing: "border-box",
    // When viewport is less than 848px (816px content + 32px padding), force column
    "@media (max-width: 848px)": {
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      maxWidth: "400px",
    },
  },
  profileCard: {
    borderRadius: theme.shape.borderRadius * 2,
    overflow: "hidden",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    display: "flex",
    flexDirection: "column",
    width: "400px",
    height: "500px",
    flexShrink: 0, // Don't shrink cards
    flexBasis: "400px", // Base size
    maxWidth: "100%", // Prevent overflow when container is smaller
    boxSizing: "border-box",
    "@media (max-width: 848px)": {
      width: "100%",
      flexBasis: "100%",
    },
  },
  blankCard: {
    borderRadius: theme.shape.borderRadius * 2,
    overflow: "hidden",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    backgroundColor: "#f5f5f5",
    display: "flex",
    flexDirection: "column",
    height: "500px",
    width: "400px",
    flexShrink: 0, // Don't shrink cards
    flexBasis: "400px", // Base size
    maxWidth: "100%", // Prevent overflow when container is smaller
    boxSizing: "border-box",
    "@media (max-width: 848px)": {
      width: "100%",
      flexBasis: "100%",
    },
  },
  historyCard: {
    borderRadius: theme.shape.borderRadius * 2,
    overflow: "hidden",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    padding: theme.spacing(2),
    // Match the width of cardsContainer - use same responsive approach
    width: "100%", // Take full width of contentWrapper
    maxWidth: `${400 + theme.spacing(2) + 400}px`, // Match max width when cards are side by side
    boxSizing: "border-box", // Ensure padding is included in width calculation
    "@media (max-width: 848px)": {
      maxWidth: "400px", // Match single card width when stacked
    },
  },
  tradeInContent: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tradeInImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  tradeInButton: {
    position: "absolute",
    bottom: theme.spacing(3),
    left: "50%",
    transform: "translateX(-50%)",
    minWidth: 150,
    zIndex: 1,
  },
  buttonLabel: {
    fontWeight: "bold",
  },
  modalPaper: {
    padding: theme.spacing(3),
    minWidth: 400,
    maxWidth: 500,
    outline: "none",
    backgroundColor: "white",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  modalTitle: {
    color: "rgb(100, 100, 100)",
  },
  modalContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  modalForm: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  modalAmountInput: {
    width: "100%",
  },
  modalMessageInput: {
    width: "100%",
  },
  modalAutocomplete: {
    width: "100%",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  modalCancelButton: {
    minWidth: 100,
  },
  modalDistributeButton: {
    minWidth: 100,
  },
  errorMessage: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    marginLeft: 0,
  },
  avatarSection: {
    background: "#FFD079",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
    position: "relative",
  },
  avatar: {
    width: theme.spacing(12.5),
    height: theme.spacing(12.5),
    border: "3px solid rgba(255, 255, 255, 0.3)",
  },
  backArrow: {
    position: "absolute",
    left: theme.spacing(2),
    border: `1px solid ${theme.palette.primary.main}`,
  },
  forwardArrow: {
    position: "absolute",
    right: theme.spacing(2),
    border: `1px solid ${theme.palette.primary.main}`,
  },
  carouselContainer: {
    width: "100%",
  },
  detailsSection: {
    backgroundColor: "white",
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  userName: {
    fontWeight: "bold",
    color: "rgb(100, 100, 100)",
    marginBottom: theme.spacing(2),
    textAlign: "center",
  },
  historyAvatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  avatarCell: {
    width: "1%",
    whiteSpace: "nowrap",
    padding: theme.spacing(1),
    fontSize: "1.1rem",
  },
  amountCell: {
    width: "1%",
    whiteSpace: "nowrap",
    fontSize: "1.4rem",
    fontWeight: 600,
  },
  dateCell: {
    width: "1%",
    whiteSpace: "nowrap",
    fontSize: "1.1rem",
  },
  descriptionCell: {
    width: "auto",
    fontSize: "1.1rem",
  },
}));
